import type { RequestContext } from "../../../application/context/request-context.js";
import type { AvrReport } from "../../../domain/engineering/avr-report-domain.js";
import type { AvrReportRepository } from "../../../repositories/operational-repositories.js";

const STORAGE_KEY = "avr_reports";

/** Espelhado em localStorage, como os demais repositórios do app real — F5 preserva o rascunho. */
export class InMemoryAvrReportRepository implements AvrReportRepository {
  private reports = new Map<string, AvrReport>();

  constructor() {
    this.loadFromStorage();
  }

  private loadFromStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const parsed: AvrReport[] = JSON.parse(stored);
          for (const r of parsed) this.reports.set(r.id, r);
        }
      }
    } catch (e) {
      console.warn("Failed to load AVR reports from storage:", e);
    }
  }

  private persistStorage() {
    try {
      if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(this.reports.values())));
      }
    } catch (e) {
      // O croqui em base64 pode estourar a cota; quem salvou precisa saber.
      console.error("Failed to persist AVR reports to storage:", e);
      throw e;
    }
  }

  async findById(_context: RequestContext, id: string): Promise<AvrReport | null> {
    this.loadFromStorage();
    return this.reports.get(id) || null;
  }

  async findByEquipment(_context: RequestContext, equipmentId: string): Promise<readonly AvrReport[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values()).filter(r => String(r.equipmentId ?? "") === equipmentId);
  }

  async listByClient(_context: RequestContext, clientId: string): Promise<readonly AvrReport[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values()).filter(r => r.clientId === clientId);
  }

  async listAll(): Promise<readonly AvrReport[]> {
    this.loadFromStorage();
    return Array.from(this.reports.values());
  }

  async save(_context: RequestContext, report: AvrReport): Promise<void> {
    this.reports.set(report.id, { ...report });
    this.persistStorage();
  }

  /** Exclusão definitiva — só o serviço decide quando ela é permitida. */
  async delete(_context: RequestContext, id: string): Promise<void> {
    this.loadFromStorage();
    this.reports.delete(id);
    this.persistStorage();
  }
}
